/**
 * CONTRACT.md §2.2 — Region, P8's primary tool.
 *
 * "ONE border around a group of related facts; children separated by `--sp-4`
 * of space." §4.4: reach for this **before** three sibling cards — borders
 * separate, whitespace groups.
 *
 * `RegionHead` takes its title and its note as props rather than as children,
 * because app.css styles them by element (`.region__head h2`, `.region__head
 * h3`) and a page that had to write the `<h2>` itself would be writing markup.
 * `pushNote` is the `.push` the contract's own example carries.
 */

import type { ReactNode } from 'react'

import { cx, type Common } from './common.js'

export type RegionProps = Common & {
  /** `.region--bare` — same rhythm, no border and no fill. */
  readonly bare?: boolean | undefined
  readonly children: ReactNode
}

export function Region({ bare, children, ...common }: RegionProps) {
  return (
    <section className={cx('region', bare && 'region--bare')} {...common}>
      {children}
    </section>
  )
}

export type RegionHeadProps = Common & {
  readonly title: ReactNode
  /** `h2` by default; `3` for a region nested under a section that already has one. */
  readonly level?: 2 | 3 | undefined
  readonly note?: ReactNode | undefined
  /** `.push` on the note — the contract's example, "This month … August 2026". */
  readonly pushNote?: boolean | undefined
}

export function RegionHead({ title, level, note, pushNote, ...common }: RegionHeadProps) {
  const Heading = level === 3 ? 'h3' : 'h2'
  return (
    <div className="region__head" {...common}>
      <Heading>{title}</Heading>
      {note === undefined ? null : <span className={cx('muted', pushNote && 'push')}>{note}</span>}
    </div>
  )
}

export type RegionColsProps = Common & { readonly children: ReactNode }

/** Auto-fit columns inside a region. No borders between them, only space. */
export function RegionCols({ children, ...common }: RegionColsProps) {
  return (
    <div className="region__cols" {...common}>
      {children}
    </div>
  )
}

/** A bare hairline, for when two parts of a region really are different things. */
export function Rule(props: Common) {
  return <hr className="rule" {...props} />
}
